<template>
    <slot v-bind="{
        ...props,
        ...state,
        setContainerElement,
        setItemElement,
        handleInput,
        handleKeyDown,
        handleFocus,
        handleBlur,
        handleMouseOver,
        handleMouseOut,
    }" />
</template>

<script setup>
import { reactive } from 'vue';
import { isString, debounce } from 'lodash';
import { flatten } from 'q-flat';

const props = defineProps({
    engineKey: {
        type: String,
        required: true,
    },
    siteIdentifier: {
        type: String,
        required: true,
    },
    queryParam: {
        type: String,
        default: 'q',
    },
    searchQuery: {
        type: String,
    },
});

const cache = {}
const minLength = 2;
const maxSuggestions = 10;
const fields = ['sections'];
const decoder = document.createElement('textarea');

const state = reactive({
    searchQuery: props.searchQuery,
    suggestedQuery: null,
    suggestions: [],
});

let containerElement = null;
let itemElements = [];

const setContainerElement = element => {
    containerElement = element;
}

const setItemElement = (element, index) => {
    itemElements[index] = element ?? null;
}

const showSuggestions = () => {
    containerElement.classList.add('is-active');
}

const hideSuggestions = () => {
    containerElement.classList.remove('is-active');
    state.suggestedIndex = null;
}

const resetSuggestions = () => {
    state.suggestions = [];
    state.suggestedIndex = null;
    hideSuggestions();
}

const decode = value => {
    decoder.innerHTML = value;

    if (decoder.childNodes.length === 0) {
        return '';
    }

    return decoder.childNodes[0].nodeValue.trim();
}

const getSuggestions = query => {
    const key = query.trim().toLowerCase();

    if (key in cache) {
        return cache[key];
    }

	let params = {
    	q: query,
        engine_key: props.engineKey,
        spelling: 'always',
        per_page: 50,
        filters: {
            page: {
                site: props.siteIdentifier,
            },
        },
    }

    params = flatten(params);

	const url = new URL('https://api.swiftype.com/api/v1/public/engines/search.json');

    for (const [ key, value ] of Object.entries(params)) {
        url.searchParams.append(key.replace(/\[\d+\]/, '[]'), value);
    }

	cache[key] = fetch(url).then(response => { // eslint-disable-line compat/compat
        return response.json();
    }).then(response => {
        let suggestions = [];

        if ('spelling_suggestion' in response.info.page) {
            suggestions.push(
                response.info.page.spelling_suggestion.text.trim()
            );
        }

        for (const page of response.records.page) {
            for (const field in page.highlight) {
                if (fields.indexOf(field) === -1) {
                    continue;
                }

                let suggestion = page.highlight[field];

                // Lower case
                suggestion = suggestion.toLowerCase();

                // Split by things
                suggestion = suggestion.split('<em>');

                // Remove first because trash
                suggestion.shift();

                suggestion = suggestion.map(item => {
                    // Remove the remaining closing em
                    item = item.replace(/<\/em>/g, '');

                    // Remove entities
                    item = decode(item);

                    // Remove other trash
                    item = item.replace(/[^A-Za-z0-9 -]/g, '');

                    return item;
                });

                for (let i = 0; i < suggestion.length - 1; i++) {
                    if (suggestion[i] && suggestion[i].endsWith('-')) {
                        suggestion[i] = suggestion[i] + suggestion[i + 1];
                        suggestion[i + 1] = '';
                    }
                }

                // Do stuff
                for (let item of suggestion) {
                    // Replace double spaces
                    item = item.replace(/\s{2,}/, ' ');

                    if (item && suggestions.indexOf(item) === -1) {
                        suggestions.push(item);
                    }
                }
            }
        }

        // Remove large suggestions
        suggestions = suggestions.filter(suggestion => {
            return suggestion.split(' ').length < 8;
        });

        // Sort by size
        suggestions.sort((a, b) => {
            return a.length - b.length;
        });

        // Limit results
        suggestions = suggestions.splice(0, maxSuggestions);

        return suggestions.map(suggestion => {
            return {
                url: '/search?q=' + encodeURIComponent(suggestion),
                value: suggestion,
                label: suggestion.replace(new RegExp(query, 'ig'), '<b>$&</b>'),
            }
        });
    });

    return cache[key];
}

const processQuery = debounce(query => {
    getSuggestions(query).then(suggestions => {
        if (suggestions.length) {
            state.suggestions = suggestions;
            showSuggestions();
        }
    });
}, 250);

const updateSelection = () => {
    for (const [ index, element ] of itemElements.entries()) {
        if (index === state.suggestedIndex) {
            element.classList.add('is-selected');
        } else {
            element.classList.remove('is-selected');
        }
    }
}

const selectNext = () => {
    if (state.suggestedIndex === null) {
        state.suggestedIndex = 0;
    } else {
        state.suggestedIndex++;

        if (state.suggestedIndex > (state.suggestions.length - 1)) {
            state.suggestedIndex = null;
        }
    }

    updateSelection();
}

const selectPrevious = () => {
    if (state.suggestedIndex === null) {
        state.suggestedIndex = state.suggestions.length - 1;
    } else {
        state.suggestedIndex--;

        if (state.suggestedIndex === -1) {
            state.suggestedIndex = null;
        }
    }

    updateSelection();
}

const handleInput = event => {
    state.searchQuery = event.target.value;

    if (isString(state.searchQuery) === false || state.searchQuery.length < minLength) {
        return resetSuggestions();
    }

    processQuery(state.searchQuery);
}

const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        selectNext();
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault();
        selectPrevious();
    }
}

const handleFocus = event => {
    if (!state.searchQuery) {
        return;
    }

    if (event.currentTarget.value !== state.searchQuery) {
        return;
    }

    if (state.suggestions.length) {
        showSuggestions();
    }
}

const handleBlur = () => {
    setTimeout(hideSuggestions, 250);
}

const handleMouseOver = event => {
    event.target.parentElement.classList.add('is-selected');
}

const handleMouseOut = event => {
    event.target.parentElement.classList.remove('is-selected');
}
</script>
